// 3-step layout-import wizard: upload → preview → result. The chosen
// File is held in component state so commit replays the exact bytes that
// preview hashed into the parse_token (edit-between → 409 on the server).

import { useState } from "react";

import { FileDropzone } from "../../components/forms/FileDropzone";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import type { CommitResponse, PreviewResponse } from "../../api/types/layoutImport";

import { PreviewDiffTable } from "./PreviewDiffTable";
import { downloadLayoutTemplate } from "./templateCsv";
import { useLayoutImport } from "./useLayoutImport";

interface LayoutImportModalProps {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
  open: boolean;
  onClose: () => void;
  onCommitted: () => void; // invalidate devices/floors/areas caches
}

export function LayoutImportModal({
  accountSlug,
  tenantSlug,
  siteSlug,
  open,
  onClose,
  onCommitted,
}: LayoutImportModalProps) {
  const { preview, commit } = useLayoutImport(accountSlug, tenantSlug, siteSlug);
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [result, setResult] = useState<CommitResponse | null>(null);
  const [applyUpdates, setApplyUpdates] = useState(false);

  const busy = preview.isPending || commit.isPending;

  function reset() {
    setFile(null);
    setPreviewData(null);
    setResult(null);
    setApplyUpdates(false);
    preview.reset();
    commit.reset();
  }

  function close() {
    if (busy) return;
    reset();
    onClose();
  }

  function onFile(f: File) {
    setFile(f);
    setResult(null);
    preview.mutate(f, { onSuccess: setPreviewData });
  }

  function confirm() {
    if (!file || !previewData) return;
    commit.mutate(
      { file, parseToken: previewData.parse_token, applyUpdates },
      { onSuccess: setResult },
    );
  }

  const hasConflicts = !!previewData && previewData.conflicts.length > 0;
  // Errors + empty always block. Conflicts block UNLESS the operator opted
  // into update mode (upsert-only — conflicts become in-place updates).
  const blockingErrors =
    !!previewData &&
    (previewData.errors.length > 0 ||
      previewData.rows.length === 0 ||
      (hasConflicts && !applyUpdates));

  return (
    <Modal
      open={open}
      onClose={close}
      closeDisabled={busy}
      title={`Import layout · ${siteSlug}`}
      width={720}
    >
      {/* Step 3: result */}
      {result ? (
        <div>
          <p style={{ marginTop: 0 }}>
            Imported <strong>{result.devices_added}</strong> new device(s)
            {result.devices_updated > 0 && (
              <> · <strong>{result.devices_updated}</strong> updated</>
            )}
            , <strong>{result.floors_added}</strong> floor(s),{" "}
            <strong>{result.areas_added}</strong> area(s).
          </p>
          <div style={footer}>
            <button
              type="button"
              style={ghostBtn}
              disabled
              title="Printable label sheet ships in B.3"
            >
              Download label sheet (B.3)
            </button>
            <button
              type="button"
              style={primaryBtn}
              onClick={() => {
                onCommitted();
                close();
              }}
            >
              Done
            </button>
          </div>
        </div>
      ) : previewData ? (
        /* Step 2: preview */
        <div>
          <Summary data={previewData} />
          {previewData.errors.length > 0 && (
            <div style={{ margin: "12px 0" }}>
              <ErrorList title="Validation errors" items={previewData.errors} />
            </div>
          )}
          {commit.error != null && (
            <div style={{ margin: "12px 0" }}>
              <ErrorBanner error={commit.error} title="Commit failed" />
            </div>
          )}
          {hasConflicts && (
            <label style={updateToggle}>
              <input
                type="checkbox"
                checked={applyUpdates}
                onChange={(e) => setApplyUpdates(e.target.checked)}
              />
              Apply updates to existing devices ({previewData.conflicts.length} changed).
              Upsert-only — nothing is deleted.
            </label>
          )}
          <PreviewDiffTable rows={previewData.rows} applyUpdates={applyUpdates} />
          <div style={footer}>
            <button type="button" style={ghostBtn} disabled={busy} onClick={reset}>
              Back
            </button>
            <button
              type="button"
              style={blockingErrors || busy ? disabledBtn : primaryBtn}
              disabled={blockingErrors || busy}
              onClick={confirm}
              title={
                blockingErrors
                  ? "Resolve conflicts/errors before importing"
                  : undefined
              }
            >
              {commit.isPending ? "Importing…" : "Confirm import"}
            </button>
          </div>
        </div>
      ) : (
        /* Step 1: upload */
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: "0.82rem", color: "var(--text-dim)" }}>
              One row per device. Floors and areas are created from the rows.
            </span>
            <button type="button" style={linkBtn} onClick={downloadLayoutTemplate}>
              Download CSV template
            </button>
          </div>
          <FileDropzone onFile={onFile} disabled={preview.isPending} />
          {preview.isPending && (
            <p style={{ color: "var(--text-dim)", marginTop: 12 }}>Parsing…</p>
          )}
          {preview.error != null && (
            <div style={{ marginTop: 12 }}>
              <ErrorBanner error={preview.error} title="Preview failed" />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function Summary({ data }: { data: PreviewResponse }) {
  const s = data.summary;
  return (
    <p style={{ marginTop: 0 }}>
      <strong>{s.devices_new}</strong> new · <strong>{s.devices_existing}</strong>{" "}
      existing · <strong style={{ color: s.devices_conflict ? "var(--crit)" : undefined }}>
        {s.devices_conflict}
      </strong>{" "}
      conflict · {s.floors_new} new floor(s) · {s.areas_new} new area(s)
    </p>
  );
}

function ErrorList({
  title,
  items,
}: {
  title: string;
  items: { row_number: number; message: string }[];
}) {
  return (
    <div
      style={{
        border: "1px solid var(--crit)",
        borderRadius: 6,
        padding: "8px 12px",
        background: "rgba(248, 113, 113, 0.08)",
      }}
    >
      <strong style={{ fontSize: "0.85rem" }}>{title}</strong>
      <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: "0.82rem" }}>
        {items.map((e, i) => (
          <li key={i}>
            {e.row_number > 0 ? `Row ${e.row_number}: ` : ""}
            {e.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

const footer: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 16,
};

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};

const disabledBtn: React.CSSProperties = {
  ...primaryBtn,
  opacity: 0.5,
  cursor: "not-allowed",
};

const updateToggle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: "4px 0 12px",
  fontSize: "0.82rem",
  color: "var(--text-dim)",
};

const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "var(--accent)",
  fontSize: "0.82rem",
  cursor: "pointer",
  textDecoration: "underline",
};

const ghostBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--text)",
  border: "1px solid var(--border)",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  cursor: "pointer",
};
